/**
 * Product analytics. Events go to PostHog and are mirrored to gtag so the
 * existing GA4 / Google Ads conversions (sign_up, begin_checkout, purchase) keep working.
 */
import posthog from 'posthog-js';
import { POSTHOG_HOST, POSTHOG_KEY, POSTHOG_UI_HOST } from './const';
import { hasAnalyticsConsent } from './analyticsConsent';

export const EXPERIMENT_STORAGE_KEY = 'calensync-experiments';
const IDENTIFIED_USER_KEY = 'calensync-analytics-user';

export type AnalyticsParams = Record<string, string | number | boolean | undefined | null>;

type ExperimentAssignment = {
    experiment_id: string;
    variant: string;
};

let posthogReady = false;

/**
 * Starts PostHog once, in the browser only. Until the visitor accepts the consent banner,
 * PostHog runs in cookieless mode: nothing is written to cookies or storage and visitors
 * are counted with a server-side hash. Cookieless mode must be enabled in the PostHog
 * project settings, or those events are dropped.
 */
export function initAnalytics(): void {
    if (posthogReady || typeof window === 'undefined' || !POSTHOG_KEY) {
        return;
    }
    posthogReady = true;
    const consented = hasAnalyticsConsent();
    posthog.init(POSTHOG_KEY, {
        api_host: POSTHOG_HOST,
        ui_host: POSTHOG_UI_HOST,
        defaults: '2026-08-30',
        capture_pageview: 'history_change',
        capture_pageleave: true,
        person_profiles: 'identified_only',
        cookieless_mode: 'on_reject',
        opt_out_capturing_by_default: !consented,
        disable_session_recording: true,
        loaded: (instance) => {
            if (consented && !instance.has_opted_in_capturing()) {
                instance.opt_in_capturing();
            }
            const experiment = experimentParams();
            if (Object.keys(experiment).length > 0) {
                instance.register(experiment);
            }
        },
    });
}

/** Switches PostHog from cookieless to normal tracking after the visitor accepts. */
export function optInAnalytics(): void {
    initAnalytics();
    if (posthogReady) {
        posthog.opt_in_capturing();
    }
}

/** Keeps PostHog cookieless after the visitor declines. */
export function optOutAnalytics(): void {
    initAnalytics();
    if (posthogReady) {
        posthog.opt_out_capturing();
    }
}

function readActiveExperiment(): ExperimentAssignment | null {
    if (typeof window === 'undefined') {
        return null;
    }
    try {
        const raw = localStorage.getItem(EXPERIMENT_STORAGE_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw) as { experiment_id?: string; variant?: string };
        if (!parsed.experiment_id || !parsed.variant) {
            return null;
        }
        return { experiment_id: parsed.experiment_id, variant: parsed.variant };
    } catch {
        return null;
    }
}

function experimentParams(): AnalyticsParams {
    const assignment = readActiveExperiment();
    if (!assignment) {
        return {};
    }
    return {
        experiment_id: assignment.experiment_id,
        variant: assignment.variant,
    };
}

export function trackEvent(name: string, params?: AnalyticsParams): void {
    if (typeof window === 'undefined') {
        return;
    }
    const payload: Record<string, string | number | boolean> = {};
    for (const [key, value] of Object.entries({ ...experimentParams(), ...params })) {
        if (value !== undefined && value !== null) {
            payload[key] = value;
        }
    }
    initAnalytics();
    if (posthogReady) {
        posthog.capture(name, payload);
    }
    if (typeof window.gtag === 'function') {
        window.gtag('event', name, payload);
    }
}

export function setAnalyticsUserId(userId: string | null): void {
    if (typeof window === 'undefined') {
        return;
    }
    initAnalytics();
    if (posthogReady) {
        if (userId) {
            posthog.identify(userId);
        } else {
            posthog.reset();
        }
    }
    if (typeof window.gtag === 'function') {
        window.gtag('set', { user_id: userId ?? undefined });
    }
}

/** Fire sign_up once for new accounts, login once per browser for returning users. */
export function trackAuthIdentify(user: { uuid: string; date_created: Date }): void {
    if (typeof window === 'undefined') {
        return;
    }
    setAnalyticsUserId(user.uuid);

    let previouslyIdentified: string | null = null;
    try {
        previouslyIdentified = localStorage.getItem(IDENTIFIED_USER_KEY);
    } catch {
        // ignore
    }

    if (previouslyIdentified === user.uuid) {
        return;
    }

    const createdMs = user.date_created.getTime();
    const isLikelyNew = Number.isFinite(createdMs) && Date.now() - createdMs < 10 * 60 * 1000;

    if (isLikelyNew) {
        trackEvent('sign_up', { method: 'oauth' });
    } else {
        trackEvent('login', { method: 'oauth' });
    }

    try {
        localStorage.setItem(IDENTIFIED_USER_KEY, user.uuid);
    } catch {
        // ignore
    }
}

/** Forget the user on logout so the next person on this browser isn't merged into them. */
export function trackLogout(): void {
    trackEvent('logout');
    setAnalyticsUserId(null);
    try {
        localStorage.removeItem(IDENTIFIED_USER_KEY);
    } catch {
        // ignore
    }
}

export function trackConsentChoice(granted: boolean): void {
    trackEvent('analytics_consent', { granted });
}

export function trackCtaClick(page: string, location: string): void {
    trackEvent('cta_click', { page, location });
}

export function trackLoginClick(provider: string): void {
    trackEvent('login_click', { provider });
}

export function trackCalendarConnectClick(provider: string): void {
    trackEvent('calendar_connect_click', { provider });
}

export function trackCalendarConnected(provider: string): void {
    trackEvent('calendar_connected', { provider });
}

export function trackSyncRuleCreated(source: string, isFirst?: boolean): void {
    trackEvent('sync_rule_created', {
        source,
        ...(isFirst === undefined ? {} : { is_first: isFirst }),
    });
}

export function trackPricingViewed(page: string): void {
    trackEvent('pricing_viewed', { page });
}

export function trackBillingIntervalChanged(interval: 'month' | 'year', page: string): void {
    trackEvent('billing_interval_changed', { interval, page });
}

export function trackBeginCheckout(plan: string): void {
    trackEvent('begin_checkout', { plan });
}

/** Paddle overlay closed without paying: the main checkout drop-off signal. */
export function trackCheckoutClosed(source: string): void {
    trackEvent('checkout_closed', { source });
}

export function trackCheckoutPaymentFailed(source: string): void {
    trackEvent('checkout_payment_failed', { source });
}

export function trackPurchase(transactionId?: string): void {
    trackEvent('purchase', transactionId ? { transaction_id: transactionId } : undefined);
}

export function trackSubscriptionCancelClick(): void {
    trackEvent('subscription_cancel_click');
}

export function trackFeatureInterest(name: string): void {
    trackEvent('feature_interest', { name });
}
